<script setup lang="ts" generic="T extends ZodRawShape">
import type { ZodAny, ZodObject, ZodRawShape } from "zod";
import type { Config, ConfigItem, Shape } from "./interface";
import { FieldContextKey, useField } from "vee-validate";
import { ChevronDown } from "lucide-vue-next";
import { computed, provide, ref } from "vue";
import { FormItem } from "~/components/ui/form";
import AutoFormField from "./AutoFormField.vue";
import AutoFormLabel from "./AutoFormLabel.vue";
import {
  beautifyObjectName,
  getBaseSchema,
  getBaseType,
  getDefaultValueInZodStack,
} from "./utils";

const props = defineProps<{
  fieldName: string;
  required?: boolean;
  config?: Config<T>;
  schema?: ZodObject<T>;
  disabled?: boolean;
}>();

const shapes = computed(() => {
  // @ts-expect-error ignore {} not assignable to object
  const val: { [key in keyof T]: Shape } = {};

  if (!props.schema) return;
  const shape = getBaseSchema(props.schema)?.shape;
  if (!shape) return;
  Object.keys(shape).forEach((name) => {
    const item = shape[name] as ZodAny;
    const baseItem = getBaseSchema(item) as ZodAny;
    let options =
      baseItem && "values" in baseItem._def
        ? (baseItem._def.values as string[])
        : undefined;
    if (!Array.isArray(options) && typeof options === "object")
      options = Object.values(options);

    val[name as keyof T] = {
      type: getBaseType(item),
      default: getDefaultValueInZodStack(item),
      options,
      required: !["ZodOptional", "ZodNullable"].includes(item._def.typeName),
      schema: item,
    };
  });
  return val;
});

const fieldContext = useField(props.fieldName);
// @ts-expect-error ignore missing `id`
provide(FieldContextKey, fieldContext);

// This section used to be a Reka-UI <Accordion> (type="single" as-child +
// AccordionItem/AccordionTrigger/AccordionContent). Live testing found that
// setup produced an intermittently non-functional trigger: a real click
// reliably bubbled to the trigger <button> (confirmed with a directly
// attached listener), but the accordion's own open/closed state did not
// consistently flip on that same click -- sometimes it worked, most times
// it didn't, with no reliable repro condition (not hydration mismatches,
// not disabled state, not event propagation -- all individually ruled out
// live). Rather than depend on that unreliable internal wiring, this is a
// small hand-rolled disclosure widget: a plain boolean ref toggled by a
// plain @click handler. It is simple enough to be deterministic, and
// intentionally forgoes the open/close slide animation (plain v-show)
// since that animation depended on Reka's internal collapsible height
// tracking, which the previous implementation shared with the trigger.
const isOpen = ref(false);
function toggleOpen() {
  if (props.disabled) return;
  isOpen.value = !isOpen.value;
}
</script>

<template>
  <section>
    <slot v-bind="props">
      <FormItem class="w-full">
        <div class="border-none">
          <h3 class="flex">
            <button
              type="button"
              class="flex flex-1 items-center justify-between py-4 text-sm font-medium transition-all hover:underline"
              :aria-expanded="isOpen"
              :disabled="disabled"
              @click="toggleOpen"
            >
              <AutoFormLabel class="text-base" :required="required">
                {{
                  config?.label ||
                  schema?.description ||
                  beautifyObjectName(fieldName)
                }}
              </AutoFormLabel>
              <ChevronDown
                class="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200"
                :class="{ 'rotate-180': isOpen }"
              />
            </button>
          </h3>
          <div v-show="isOpen" class="space-y-5 p-1 pb-4 pt-0 text-sm">
            <template v-for="(shape, key) in shapes" :key="key">
              <AutoFormField
                :config="config?.[key as keyof typeof config] as ConfigItem"
                :field-name="`${fieldName}.${key.toString()}`"
                :label="key.toString()"
                :shape="shape"
              />
            </template>
          </div>
        </div>
      </FormItem>
    </slot>
  </section>
</template>
